import { useContext } from "react";
import { LocaleContext } from "../../../routing/LangRouter";
import { Divider, Grid } from "@mui/material";
import { numberWithCommas } from "../../../utils/utility";
import { convertToJalali } from "../../../utils/date";
import { useTranslation } from "react-i18next";

//import general components
import BaseRating from "../base-rating/base-rating";

//import images and icons
import { Star1 } from "iconsax-react";
import roomImage from "../../../assets/imgs/room-image.png";
import noImage from "../../../assets/imgs/no-image.png";

//import styles
import "./minimal-hotel-card.scss";

function HotelCard({ hotelItem, selectedRooms, selectedIntRooms, name, handleDelete }) {
    //general
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));

    const { isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation"));

    return (
        <Grid
            className="minimal-hotel-card"
            container
            item
            xs={12}
            rowGap={2}
            justifyContent="space-between"
            alignItems="flex-start"
            mb={{ xs: 5, md: 0 }}
        >
            <Grid container item xs={12} md={3} rowGap={2} justifyContent={{ xs: "space-between", md: "center" }} alignItems="center">
                <Grid
                    className="minimal-hotel-card__hotel-image"
                    component="img"
                    src={
                        hotelItem?.images?.length
                            ? isHotelInternational
                                ? hotelItem.images[0]?.image
                                : "https://api.atripa.com" + hotelItem?.images[0]?.image
                            : noImage
                    }
                    container
                    item
                    xs="auto"
                    md={12}
                    order={{ xs: 2, md: 1 }}
                />

                <Grid container item xs={8} md={12} columnGap={{ xs: 2, md: 0 }} rowGap={{ xs: 1, md: 2 }} order={{ xs: 3, md: 2 }}>
                    <Grid
                        component="span"
                        className="minimal-hotel-card__hotel-name"
                        container
                        item
                        xs="auto"
                        md={6}
                        justifyContent="center"
                        alignItems="center"
                    >
                        {hotelItem?.name}
                    </Grid>

                    <Grid container item xs="auto" md={6} justifyContent="center" alignItems="center">
                        <BaseRating value={hotelItem?.star} icon={Star1} emptyIcon={Star1} color="var(--golden-color)" size="20" readOnly={true} />
                    </Grid>

                    <Grid
                        component="span"
                        className="minimal-hotel-card__date"
                        container
                        item
                        xs={12}
                        justifyContent={{ xs: "flex-start", md: "center" }}
                    >
                        {t("from")} {locale === "en" ? hotelInformation.checkIn : convertToJalali(hotelInformation.checkIn)}{" "}
                        {t("to-2")} {locale === "en" ? hotelInformation.checkOut : convertToJalali(hotelInformation.checkOut)}
                    </Grid>
                </Grid>

                <Grid container item xs={12} md={6} rowGap={1} justifyContent={{ xs: "space-between", md: "flex-end" }} order={{ xs: 1, md: 3 }}>
                    <Grid
                        className="minimal-hotel-card__title"
                        component="span"
                        container
                        item
                        xs="auto"
                        display={{ xs: "flex", md: "none" }}
                        justifyContent="center"
                    >
                        {t("hotel")}
                    </Grid>
                    <Grid
                        className="minimal-hotel-card__delete"
                        component="span"
                        container
                        item
                        xs="auto"
                        md={12}
                        justifyContent="center"
                        // alignItems="center"
                        onClick={() => handleDelete(name)}
                    >
                        {t("editHotel")}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container item xs={12} md={8}>
                <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                    <span className="minimal-hotel-card__room-title">{t("rooms")}</span>
                    <Divider width="88%" />
                </Grid>

                {selectedRooms &&
                    selectedRooms?.map(room => {
                        return (
                            <>
                                <Grid
                                    className="minimal-hotel-card__room"
                                    container
                                    item
                                    xs={12}
                                    rowGap={1}
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid component="img" src={roomImage} container item xs={1} />
                                    <Grid container item xs={12} md={3}>
                                        {room.count} * {room.name}
                                    </Grid>
                                    <Grid container item xs="auto" md={2.5} justifyContent={{ xs: "flex-start", md: "center" }}>
                                        {room.planName}
                                    </Grid>
                                    <Grid container item xs="auto" md={2.5} justifyContent={{ xs: "flex-start", md: "center" }}>
                                        {room.extraCapacity === 0 ? t("noExtraCapacity") : `${t("extraCapacity")}:` + room.extraCapacity}
                                    </Grid>
                                    <Grid container item xs="auto" md={2.5} justifyContent="flex-end">
                                        {numberWithCommas(room.prices.total_price * room.count)} {t("currency")}
                                    </Grid>
                                </Grid>
                                <Divider width="100%" />
                            </>
                        );
                    })}

                {selectedIntRooms?.rooms &&
                    Object.keys(selectedIntRooms.rooms).length > 0 &&
                    Object.values(selectedIntRooms.rooms).map((room, index) => {
                        // Access adult_count and child_count from travelers object
                        const adultCount = Object.values(room.travelers?.adult_count || {})[0] || 0;
                        const childCount = Object.values(room.travelers?.child_count || {})[0] || 0;

                        return (
                            <div key={index}>
                                <Grid
                                    className="minimal-hotel-card__room"
                                    container
                                    item
                                    xs={12}
                                    rowGap={1}
                                    spacing={{ md: 5 }}
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid component="img" src={roomImage} container item xs={1} md={1.5} />
                                    <Grid container item xs={12} md={2.5} justifyContent={{ md: "center" }}>
                                        {room.name}
                                    </Grid>
                                    <Grid container item xs="auto" md={2.5} justifyContent={{ xs: "flex-start", md: "center" }}>
                                        {room.meal_type}
                                    </Grid>
                                    <Grid container item xs="auto" md={2.5} justifyContent={{ xs: "flex-start", md: "center" }}>
                                        {`${t("numberOfPeople")}:` + (adultCount + childCount)}
                                    </Grid>
                                    {index === Object.values(selectedIntRooms.rooms).length - 1 && selectedIntRooms.fare?.total && (
                                        <Grid container item xs="auto" md={3} justifyContent="flex-end">
                                            {numberWithCommas(selectedIntRooms.fare.total)} {t("currency")}
                                        </Grid>
                                    )}
                                </Grid>
                                <Divider width="100%" />
                            </div>
                        );
                    })}
            </Grid>
        </Grid>
    );
}

export default HotelCard;
